import Bugsnag from '@bugsnag/js'
import { Box, FormControl, RadioGroup, Typography } from '@mui/material'
import AdxButton from 'components/custom/AdxButton'
import AdxCheckbox from 'components/custom/AdxCheckbox'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import AdxFormControlLabel from 'components/custom/AdxFormControlLabel'
import AdxInput from 'components/custom/AdxInput'
import AdxRadioButton from 'components/custom/AdxRadioButton'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import React, { useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import {
  editCustomCategory,
  postCustomCategory,
} from 'services/api/modules/payroll/payroll-config/customCategoryApi'
import * as yup from 'yup'

const PayrollCategoryModal = ({ children, props, details }) => {
  const [isModalOpen, setModalOpen] = useState(false)
  
  // Debug logging
  console.log('PayrollCategoryModal props:', props)
  console.log('PayrollCategoryModal details:', details)
  
  const handleOpen = () => {
    console.log('Modal opening with props:', props)
    setModalOpen(true)
  }
  const handleClose = () => {
    setModalOpen(false)
  }
  const validationSchema = yup.object().shape({
    title: yup
      .string()
      .nullable()
      .required('This field is required')
      .matches(
        /^[a-zA-Z0-9\s.]*$/,
        'This field may contain only letters, numbers, spaces, and periods'
      ),
  })
  const queryClient = useQueryClient()
  const { mutate: addCustomCategory } = useMutation(postCustomCategory, {
    onSuccess: () => {
      queryClient.invalidateQueries('getCustomCategory')
    },
    onError: (error) => {
      if (error.status === 422) {
        console.log(error)
      } else {
        Bugsnag.notify(error.data)
      }
    },
  })
  const { mutate: changeCustomCategory } = useMutation(editCustomCategory, {
    onSuccess: () => {
      queryClient.invalidateQueries('getCustomCategory')
    },
    onError: (error) => {
      if (error.status === 422) {
        console.log(error)
      } else {
        Bugsnag.notify(error.data)
      }
    },
  })

  const handleSubmit = async (values) => {
    const {
      epf_deduction,
      socso_deduction,
      eis_deduction,
      lhdn_deduction,
      hrdf_deduction,
      custom_status,
      item_id,
      ...rest
    } = values
    
    // For "Deduction after gross salary" (custom_status === 5), set all statutory deductions to 0
    // For "Deduction before gross salary" (custom_status === 4), allow statutory deductions
    let data = {
      ...rest,
      custom_status,
      epf_deduction: custom_status === 5 ? 0 : epf_deduction ? 1 : 0,
      socso_deduction: custom_status === 5 ? 0 : socso_deduction ? 1 : 0,
      eis_deduction: custom_status === 5 ? 0 : eis_deduction ? 1 : 0,
      lhdn_deduction: custom_status === 5 ? 0 : lhdn_deduction ? 1 : 0,
      hrdf_deduction: custom_status === 5 ? 0 : hrdf_deduction ? 1 : 0,
    }
    
    if (values.item_id) {
      changeCustomCategory({ id: item_id, data })
    } else {
      addCustomCategory(data)
    }
    setModalOpen(false)
  }
  return (
    <>
      <Box onClick={handleOpen}>{children}</Box>

      <Formik
        enableReinitialize={true}
        initialValues={
          details
            ? {
                custom_status: details?.custom_status,
                item_id: details?.id,
                title: details?.title,
                epf_deduction: details?.epf?.status === 1 ? true : false,
                socso_deduction: details?.socso?.status === 1 ? true : false,
                eis_deduction: details?.eis?.status === 1 ? true : false,
                lhdn_deduction: details?.lhdn?.status === 1 ? true : false,
                hrdf_deduction: details?.hrdf?.status === 1 ? true : false,
              }
            : {
                custom_status: props?.id,
                title: '',
                epf_deduction: false,
                socso_deduction: false,
                eis_deduction: false,
                lhdn_deduction: false,
                hrdf_deduction: false,
              }
        }
        validationSchema={validationSchema}
        onSubmit={(values) => handleSubmit(values)}
        onInit={(values) => {
          console.log('Formik initialized with values:', values)
        }}
      >
        {({ values, setFieldValue, handleChange, touched, errors, handleBlur, handleSubmit }) => {
          return (
            <Form>
              <AdxFormModal
                open={isModalOpen}
                onClose={handleClose}
                handleClose={handleClose}
                title={props?.item}
                subtitle=""
                childPadding={false}
                enableFooter
                actionText="Save"
                handleAction={handleSubmit}
                cancelText="Cancel"
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ gap: 2 }}
                >
                  <Box
                    width={1}
                    display="flex"
                    flexDirection="column"
                    sx={{ gap: 2, px: 3, py: 1 }}
                  >
                    <Box sx={{}}>
                      <Field
                        name="title"
                        as={AdxInput}
                        type="standard"
                        placeholder="Custom name"
                        // charLimit={50}
                        label="Title"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        errorMessage={<ErrorMessage name="title" component={AdxErrorMessage} />}
                        isErrorShown={touched?.title && errors?.title}
                      />
                    </Box>
                    {(values?.custom_status === 4 || values?.custom_status === 5) && (
                      <Box my={2}>
                        <Typography variant="body1">Deduction categories</Typography>
                        <FormControl>
                          <Field
                            as={RadioGroup}
                            name={`custom_status`}
                            value={values?.custom_status}
                            onChange={(e) =>
                              setFieldValue('custom_status', parseInt(e.target.value))
                            }
                            sx={{ display: 'flex' }}
                          >
                            <AdxFormControlLabel
                              type="no-fill"
                              selected={values?.custom_status === 4}
                              value={4}
                              control={<AdxRadioButton />}
                              label="Deduction before gross salary"
                            />
                            <AdxFormControlLabel
                              type="no-fill"
                              selected={values?.custom_status === 5}
                              value={5}
                              control={<AdxRadioButton />}
                              label="Deduction after gross salary"
                            />
                          </Field>
                        </FormControl>
                      </Box>
                    )}

                    {/* Show statutory eligibility for:
                        - Non-deductions (custom_status !== 4 && !== 5)
                        - "Deduction before gross salary" (custom_status === 4)
                        Hide for "Deduction after gross salary" (custom_status === 5)
                    */}
                    {(values?.custom_status !== 4 && values?.custom_status !== 5) || values?.custom_status === 4 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body1">Statutory eligibility</Typography>
                        <AdxFormControlLabel
                          name="epf_deduction"
                          type="no-fill"
                          control={<AdxCheckbox />}
                          checked={values.epf_deduction}
                          label={`EPF - Employees' Provident Fund`}
                          onChange={(e) => setFieldValue('epf_deduction', e.target.checked)}
                        />
                        <AdxFormControlLabel
                          name="socso_deduction"
                          type="no-fill"
                          control={<AdxCheckbox />}
                          checked={values.socso_deduction}
                          label={`SOCSO - Social Security Organisation`}
                          onChange={(e) => setFieldValue('socso_deduction', e.target.checked)}
                        />
                        <AdxFormControlLabel
                          name="eis_deduction"
                          type="no-fill"
                          control={<AdxCheckbox />}
                          checked={values.eis_deduction}
                          label={`EIS - Employment Insurance System`}
                          onChange={(e) => setFieldValue('eis_deduction', e.target.checked)}
                        />
                        <AdxFormControlLabel
                          name="lhdn_deduction"
                          type="no-fill"
                          control={<AdxCheckbox />}
                          checked={values.lhdn_deduction}
                          label={`LHDN - Lembaga Hasil Dalam Negeri`}
                          onChange={(e) => setFieldValue('lhdn_deduction', e.target.checked)}
                        />
                        <AdxFormControlLabel
                          name="hrdf_deduction"
                          type="no-fill"
                          control={<AdxCheckbox />}
                          checked={values.hrdf_deduction}
                          label={`HRDF - Human Resources Development Fund`}
                          onChange={(e) => setFieldValue('hrdf_deduction', e.target.checked)}
                        />
                      </Box>
                    ) : null}
                  </Box>
                </Box>
              </AdxFormModal>
            </Form>
          )
        }}
      </Formik>
    </>
  )
}

export default PayrollCategoryModal
